import { X } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { BrandMark, IconButton } from '@/components/ui';
import { type Dashboard, useDashboard } from '@/features/dashboard/api';
import { useT } from '@/i18n';
import { useApp } from '@/lib/auth/app-context';
import { roleCan } from '@/lib/auth/permissions';
import { cn } from '@/lib/cn';
import { useFormat } from '@/lib/format/useFormat';
import { useOnline } from '@/lib/online';
import { type BadgeSource, isNavActive, type NavGroup, PLATFORM_NAV, SCHOOL_NAV } from './nav';

function badgeCounts(d: Dashboard | undefined): Partial<Record<BadgeSource, number>> {
  if (!d) return {};
  const count = (types: string[]) => d.approvals.filter((a) => types.includes(a.type)).length;
  return {
    payments: d.fees?.pendingClearance.count,
    fees: count(['DISCOUNT']),
    advances: count(['ADVANCE_DECISION', 'ADVANCE_PAYOUT']),
    payroll: count(['PAYROLL_APPROVAL', 'PAYROLL_PAYMENT']),
  };
}

/** "مدرسة النور الأهلية" → "ن": skip the word "school" and the article. */
export function schoolInitial(name: string): string {
  const word = name.trim().replace(/^(مدرسة|مدارس|School)\s+/i, '').split(/\s+/)[0] ?? '';
  return (word.startsWith('ال') && word.length > 2 ? word.charAt(2) : word.charAt(0)).toUpperCase();
}

export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const app = useApp();
  const fmt = useFormat();
  const online = useOnline();
  const { pathname } = useLocation();
  // Badges come from the office dashboard (teachers have none).
  const dashboard = useDashboard(!!app.school && roleCan(app.role, 'office'));
  const badges = badgeCounts(dashboard.data);

  const groups: NavGroup[] = (app.school ? SCHOOL_NAV : PLATFORM_NAV)
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => (!i.feature || app.features[i.feature]) && (!i.perm || roleCan(app.role, i.perm))),
    }))
    .filter((g) => g.items.length > 0);

  const school = app.school;
  const initial = schoolInitial(school?.name ?? t('app.name'));

  return (
    <>
      {open ? <div aria-hidden className="fixed inset-0 z-30 bg-ink/45 lg:hidden" onClick={onClose} /> : null}
      <aside
        aria-label={t('app.name')}
        className={cn(
          'fixed inset-y-0 start-0 z-40 flex w-[272px] flex-col gap-5 overflow-y-auto bg-sidebar px-3.5 py-5 text-sidebar-text transition-transform print:hidden',
          'lg:sticky lg:top-0 lg:z-auto lg:h-dvh lg:w-auto',
          !open && 'max-lg:invisible max-lg:ltr:-translate-x-full max-lg:rtl:translate-x-full',
        )}
      >
        <div className="flex items-center gap-3 px-1.5">
          {school?.logoUrl ? (
            <img src={school.logoUrl} alt="" className="size-10 shrink-0 rounded-md bg-white object-contain" />
          ) : !school ? (
            <BrandMark className="size-10" />
          ) : (
            <div
              aria-hidden
              className="grid size-10 shrink-0 place-items-center rounded-md bg-accent text-lg font-bold text-white"
              style={school?.primaryColor ? { background: school.primaryColor } : undefined}
            >
              {initial}
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-base font-semibold text-white">{school?.name ?? t('app.name')}</span>
            <span className="truncate text-xs text-sidebar-muted">
              {school ? (school.currentYear ? t('shell.year', { name: fmt.digits(school.currentYear.name) }) : t('shell.noYear')) : t('role.PLATFORM')}
            </span>
          </div>
          <IconButton icon={X} label={t('common.closeMenu')} onClick={onClose} className="text-sidebar-text hover:bg-white/10 hover:text-white lg:hidden" />
        </div>

        <nav className="flex flex-col gap-0.5">
          {groups.map((g) => (
            <div key={g.label} className="flex flex-col gap-0.5">
              <div className="px-3 pt-3.5 pb-1.5 text-xs font-medium text-sidebar-muted">{t(g.label)}</div>
              {g.items.map((item) => {
                const active = isNavActive(item, pathname);
                const Icon = item.icon;
                const badge = item.badge ? badges[item.badge] : undefined;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex h-11 items-center gap-3 rounded-md px-3 text-base transition-colors',
                      active ? 'bg-sidebar-active font-medium text-white' : 'hover:bg-white/5 hover:text-white',
                    )}
                  >
                    <Icon aria-hidden className={cn('size-[18px] shrink-0', active ? 'text-accent-soft' : 'text-sidebar-muted')} />
                    <span className="truncate">{t(item.label)}</span>
                    {badge ? <span className="ms-auto rounded-full bg-warning px-2 text-xs leading-5 font-medium text-white">{fmt.number(badge)}</span> : null}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="mt-auto flex flex-col gap-1 border-t border-sidebar-active px-2.5 pt-3.5 text-xs text-sidebar-muted">
          <div className="flex items-center gap-2" role="status">
            <span aria-hidden className={cn('size-2 rounded-full', online ? 'bg-accent-soft' : 'bg-warning')} />
            <span>{online ? t('shell.online') : t('shell.offline')}</span>
          </div>
        </div>
      </aside>
    </>
  );
}
